import { useMemo, useState } from 'react'
import { apiPost } from '../../lib/api'
import type { Chat, TeamMember } from '../../lib/types'
import { Avatar } from '../common'

// "Nuevo mensaje" (dm: elegir persona — incluye notas contigo mismo) y
// "Nuevo grupo" (nombre + multiselect de miembros activos).

export function NewChatModal({
  mode,
  meEmail,
  meName,
  team,
  onClose,
  onCreated,
  toast,
}: {
  mode: 'dm' | 'group'
  meEmail: string
  meName: string
  team: TeamMember[]
  onClose: () => void
  onCreated: (chat: Chat) => void
  toast: (msg: string) => void
}) {
  const [q, setQ] = useState('')
  const [name, setName] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  const candidates = useMemo(
    () =>
      team
        .filter((m) => m.isActive !== false && m.email !== meEmail)
        .filter(
          (m) =>
            !q ||
            m.name.toLowerCase().includes(q.toLowerCase()) ||
            m.email.toLowerCase().includes(q.toLowerCase()),
        ),
    [team, meEmail, q],
  )

  const createDm = async (memberEmail: string) => {
    if (busy) return
    setBusy(true)
    try {
      const { chat } = await apiPost<{ chat: Chat }>('/api/chats', { type: 'dm', memberEmail })
      onCreated(chat)
    } catch {
      toast('No se pudo abrir la conversación')
      setBusy(false)
    }
  }

  const createGroup = async () => {
    const groupName = name.trim()
    if (!groupName) {
      toast('Ponle un nombre al grupo')
      return
    }
    setBusy(true)
    try {
      const { chat } = await apiPost<{ chat: Chat }>('/api/chats', {
        type: 'group',
        name: groupName,
        members: [...selected],
      })
      onCreated(chat)
    } catch {
      toast('No se pudo crear el grupo')
      setBusy(false)
    }
  }

  const toggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email)
      else next.add(email)
      return next
    })
  }

  return (
    <div className="chat-modal-overlay modal-overlay" role="presentation" data-capa-flotante onClick={onClose}>
      <div
        className="chat-modal modal"
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'dm' ? 'Nuevo mensaje' : 'Nuevo grupo'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="chat-modal-head">
          <h2>{mode === 'dm' ? 'Nuevo mensaje' : 'Nuevo grupo'}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Cerrar">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {mode === 'group' && (
          <div className="chat-modal-row">
            <label className="field-label" htmlFor="new-group-name">
              Nombre del grupo
            </label>
            <input
              id="new-group-name"
              value={name}
              maxLength={80}
              placeholder="Equipo evento X"
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
        )}

        <div className="chat-modal-row">
          <div className="search">
            <span aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                <circle cx="11" cy="11" r="7" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            </span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar persona…"
              aria-label="Buscar persona"
              autoFocus={mode === 'dm'}
            />
          </div>

          <ul className="chat-member-list chat-pick-list">
            {mode === 'dm' && !q && (
              <li>
                <button type="button" className="chat-pick-row as-btn" disabled={busy} onClick={() => void createDm(meEmail)}>
                  <Avatar who={meName} title={meEmail} />
                  <span className="chat-member-name">{meName} (tú)</span>
                  <span className="muted">notas personales</span>
                </button>
              </li>
            )}
            {candidates.length === 0 && <li className="muted chat-pick-empty">Sin resultados</li>}
            {candidates.map((m) =>
              mode === 'dm' ? (
                <li key={m.email}>
                  <button type="button" className="chat-pick-row as-btn" disabled={busy} onClick={() => void createDm(m.email)}>
                    <Avatar who={m.name} title={m.email} />
                    <span className="chat-member-name">{m.name}</span>
                    <span className="muted">{m.email}</span>
                  </button>
                </li>
              ) : (
                <li key={m.email}>
                  <label className="chat-pick-row">
                    <input type="checkbox" checked={selected.has(m.email)} onChange={() => toggle(m.email)} />
                    <Avatar who={m.name} title={m.email} />
                    <span className="chat-member-name">{m.name}</span>
                    <span className="muted">{m.email}</span>
                  </label>
                </li>
              ),
            )}
          </ul>
        </div>

        {mode === 'group' && (
          <div className="chat-modal-foot">
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" disabled={busy || !name.trim()} onClick={() => void createGroup()}>
              Crear grupo{selected.size > 0 ? ` (${selected.size + 1})` : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
